<template>
    <div class="content_box">
        <div class="content">
            <div class="title_box">
                <div class="title">
                    <span>发票配置</span>
                </div>
                <div class="title_desc">
                    维护各站点的开票主体（公司抬头 / 地址 / 税号）与按国家的税率。生成发票时：传入 seller_id 优先，否则按订单站点自动匹配主体；税率优先取此处配置，未配置时使用内置欧盟标准税率。
                </div>
            </div>

            <el-tabs v-model="activeTab">
                <!-- ==================== 开票主体 ==================== -->
                <el-tab-pane label="开票主体" name="seller">
                    <div class="btn_box">
                        <div class="btn_right_box">
                            <div class="button button2 add" @click="openSellerCreate" v-if="canEdit">
                                <i class="el-icon-plus"></i>
                                <span>新增主体</span>
                            </div>
                        </div>
                    </div>
                    <el-table :data="sellerList" v-loading="sellerLoading" stripe style="width: 100%">
                        <el-table-column prop="site_match" label="匹配站点" width="120">
                            <template slot-scope="scope">
                                <span>{{ scope.row.site_match || '（全局兜底）' }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="legal_name" label="公司抬头" min-width="220" show-overflow-tooltip></el-table-column>
                        <el-table-column label="地址" min-width="260" show-overflow-tooltip>
                            <template slot-scope="scope">
                                {{ fullAddress(scope.row) }}
                            </template>
                        </el-table-column>
                        <el-table-column prop="ust_idnr" label="VAT / USt-IdNr." width="160" show-overflow-tooltip>
                            <template slot-scope="scope">
                                {{ scope.row.ust_idnr || '-' }}
                            </template>
                        </el-table-column>
                        <el-table-column prop="remark" label="备注" min-width="150" show-overflow-tooltip>
                            <template slot-scope="scope">
                                {{ scope.row.remark || '-' }}
                            </template>
                        </el-table-column>
                        <el-table-column label="状态" width="90">
                            <template slot-scope="scope">
                                <el-tag :type="scope.row.status === 1 ? 'success' : 'info'" size="small">
                                    {{ scope.row.status === 1 ? '启用' : '停用' }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column label="操作" width="140" fixed="right">
                            <template slot-scope="scope">
                                <el-button type="text" @click="openSellerEdit(scope.row)" v-if="canEdit">编辑</el-button>
                                <el-button type="text" class="del_btn" @click="disableSeller(scope.row)"
                                           v-if="canEdit && scope.row.status === 1">停用</el-button>
                            </template>
                        </el-table-column>
                    </el-table>
                    <div class="form_tip">站点匹配规则：发票生成时先按订单站点做包含匹配（如站点 neabot-us 命中 us），未命中则使用留空站点的主体的作为全局兜底。</div>
                </el-tab-pane>

                <!-- ==================== 税率配置 ==================== -->
                <el-tab-pane label="税率配置" name="tax">
                    <div class="btn_box">
                        <div class="btn_right_box">
                            <div class="button button2 add" @click="openTaxCreate" v-if="canEdit">
                                <i class="el-icon-plus"></i>
                                <span>新增税率</span>
                            </div>
                        </div>
                    </div>
                    <el-table :data="taxList" v-loading="taxLoading" stripe style="width: 100%">
                        <el-table-column prop="country_code" label="国家代码" width="120"></el-table-column>
                        <el-table-column label="税率" width="120">
                            <template slot-scope="scope">
                                {{ fmtRate(scope.row.tax_rate) }}
                            </template>
                        </el-table-column>
                        <el-table-column label="价格口径" width="140">
                            <template slot-scope="scope">
                                <el-tag size="small" :type="scope.row.price_included === 1 ? 'warning' : 'success'">
                                    {{ scope.row.price_included === 1 ? '价格含税（反算）' : '价外税' }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column prop="remark" label="备注" min-width="200" show-overflow-tooltip>
                            <template slot-scope="scope">
                                {{ scope.row.remark || '-' }}
                            </template>
                        </el-table-column>
                        <el-table-column label="状态" width="90">
                            <template slot-scope="scope">
                                <el-tag :type="scope.row.status === 1 ? 'success' : 'info'" size="small">
                                    {{ scope.row.status === 1 ? '启用' : '停用' }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column label="操作" width="140" fixed="right">
                            <template slot-scope="scope">
                                <el-button type="text" @click="openTaxEdit(scope.row)" v-if="canEdit">编辑</el-button>
                                <el-button type="text" class="del_btn" @click="disableTax(scope.row)"
                                           v-if="canEdit && scope.row.status === 1">停用</el-button>
                            </template>
                        </el-table-column>
                    </el-table>
                    <div class="form_tip">
                        未配置的国家自动使用内置欧盟标准税率：HU 27%、FI 25.5%、HR/DK/SE 25%、GR/RO/EE 24%、IE/PL/PT/SK 23%、IT/SI 22%、BE/CZ/LV/LT/NL/ES 21%、AT/BG/FR 20%、CY/DE 19%；非欧盟国家按无税处理。美国税取订单自带的分档税，无需配置。
                    </div>
                </el-tab-pane>
            </el-tabs>

            <!-- ==================== 主体弹窗 ==================== -->
            <Gh-Dialog size="big" ref="sellerDialog"
                       :title="sellerMode === 'create' ? '新增开票主体' : '编辑开票主体'"
                       @dialogBack="submitSeller">
                <template v-slot:content>
                    <el-form label-width="130px" :model="sellerForm">
                        <el-form-item label="匹配站点">
                            <el-input v-model="sellerForm.site_match" placeholder="如 us / de / fr，留空为全局兜底" maxlength="64"></el-input>
                        </el-form-item>
                        <el-form-item label="公司抬头" required>
                            <el-input v-model="sellerForm.legal_name" placeholder="发票上的公司全称，如 GENHIGH TECH CO., LIMITED" maxlength="128"></el-input>
                        </el-form-item>
                        <el-form-item label="街道地址">
                            <el-input v-model="sellerForm.street" placeholder="如 251 Reclamation Street" maxlength="255"></el-input>
                        </el-form-item>
                        <el-form-item label="城市">
                            <el-input v-model="sellerForm.city" placeholder="如 Mong Kok, Kowloon" maxlength="64"></el-input>
                        </el-form-item>
                        <el-form-item label="邮编">
                            <el-input v-model="sellerForm.zip" placeholder="邮政编码（可空）" maxlength="32"></el-input>
                        </el-form-item>
                        <el-form-item label="国家/地区">
                            <el-input v-model="sellerForm.country" placeholder="如 Hong Kong / Germany / United States" maxlength="64"></el-input>
                        </el-form-item>
                        <el-form-item label="国家代码">
                            <el-input v-model="sellerForm.country_code" placeholder="如 HK / DE / US" maxlength="8"></el-input>
                        </el-form-item>
                        <el-form-item label="VAT / USt-IdNr.">
                            <el-input v-model="sellerForm.ust_idnr" placeholder="增值税号，如 DE463080833（可空）" maxlength="64"></el-input>
                        </el-form-item>
                        <el-form-item label="备注">
                            <el-input v-model="sellerForm.remark" placeholder="内部备注（可空）" maxlength="255"></el-input>
                        </el-form-item>
                        <el-form-item label="启用">
                            <el-switch v-model="sellerForm.status"></el-switch>
                        </el-form-item>
                    </el-form>
                </template>
            </Gh-Dialog>

            <!-- ==================== 税率弹窗 ==================== -->
            <Gh-Dialog size="big" ref="taxDialog"
                       :title="taxMode === 'create' ? '新增税率' : '编辑税率'"
                       @dialogBack="submitTax">
                <template v-slot:content>
                    <el-form label-width="130px" :model="taxForm">
                        <el-form-item label="国家代码" required>
                            <el-input v-model="taxForm.country_code" placeholder="ISO 两位代码，如 DE / FR / ES" maxlength="8" :disabled="taxMode === 'edit'"></el-input>
                        </el-form-item>
                        <el-form-item label="税率（%）" required>
                            <div class="rate_input_box">
                                <el-input v-model="taxForm.ratePct" class="rate_input" placeholder="如 19" maxlength="6">
                                    <template slot="append">%</template>
                                </el-input>
                                <span class="form_tip">如 19 表示 19%，可填 25.5</span>
                            </div>
                        </el-form-item>
                        <el-form-item label="价格口径">
                            <el-radio-group v-model="taxForm.price_included">
                                <el-radio :label="1">价格含税（按净额反算）</el-radio>
                                <el-radio :label="0">价外税（净价外加税）</el-radio>
                            </el-radio-group>
                        </el-form-item>
                        <el-form-item label="备注">
                            <el-input v-model="taxForm.remark" placeholder="内部备注（可空）" maxlength="255"></el-input>
                        </el-form-item>
                        <el-form-item label="启用">
                            <el-switch v-model="taxForm.status"></el-switch>
                        </el-form-item>
                    </el-form>
                </template>
            </Gh-Dialog>
        </div>
    </div>
</template>

<script>
export default {
    name: 'invoice-config',
    data() {
        return {
            activeTab: 'seller',
            sellerList: [],
            sellerLoading: false,
            sellerMode: 'create',
            sellerForm: {
                id: '',
                site_match: '',
                legal_name: '',
                street: '',
                city: '',
                zip: '',
                country: '',
                country_code: '',
                ust_idnr: '',
                remark: '',
                status: true
            },
            taxList: [],
            taxLoading: false,
            taxMode: 'create',
            taxForm: {
                id: '',
                country_code: '',
                ratePct: '',
                price_included: 1,
                remark: '',
                status: true
            }
        }
    },
    computed: {
        canEdit() {
            return this.roleCheck('110-101-101-102')
        }
    },
    created() {
        this.loadSellers()
        this.loadTaxes()
    },
    methods: {
        fullAddress(row) {
            const parts = [row.street, row.zip, row.city, row.country].filter(item => item && String(item).trim())
            return parts.length ? parts.join(' ') : '-'
        },
        fmtRate(v) {
            const n = parseFloat(v)
            if (isNaN(n)) return '-'
            return (n * 100).toFixed(2).replace(/\.?0+$/, '') + '%'
        },

        // ========== 开票主体 ==========
        loadSellers() {
            this.sellerLoading = true
            this.api.listInvoiceSeller({}).then(res => {
                this.sellerList = (res && res.code === 200 && Array.isArray(res.data)) ? res.data : []
                this.sellerLoading = false
            }).catch(() => {
                this.sellerList = []
                this.sellerLoading = false
            })
        },
        resetSellerForm() {
            this.sellerForm = {
                id: '', site_match: '', legal_name: '', street: '', city: '', zip: '',
                country: '', country_code: '', ust_idnr: '', remark: '', status: true
            }
        },
        openSellerCreate() {
            this.sellerMode = 'create'
            this.resetSellerForm()
            this.$refs.sellerDialog.show = true
        },
        openSellerEdit(row) {
            this.sellerMode = 'edit'
            this.sellerForm = {
                id: row.id,
                site_match: row.site_match || '',
                legal_name: row.legal_name || '',
                street: row.street || '',
                city: row.city || '',
                zip: row.zip || '',
                country: row.country || '',
                country_code: row.country_code || '',
                ust_idnr: row.ust_idnr || '',
                remark: row.remark || '',
                status: row.status === 1
            }
            this.$refs.sellerDialog.show = true
        },
        submitSeller() {
            if (!this.sellerForm.legal_name || !this.sellerForm.legal_name.trim()) {
                this.$message.warning('请输入公司抬头')
                return
            }
            const data = {
                site_match: (this.sellerForm.site_match || '').trim(),
                legal_name: (this.sellerForm.legal_name || '').trim(),
                street: this.sellerForm.street,
                city: this.sellerForm.city,
                zip: this.sellerForm.zip,
                country: this.sellerForm.country,
                country_code: this.sellerForm.country_code,
                ust_idnr: this.sellerForm.ust_idnr,
                remark: this.sellerForm.remark,
                status: this.sellerForm.status ? 1 : 0
            }
            if (this.sellerMode === 'create') {
                this.api.addInvoiceSeller(data).then(res => {
                    if (res && res.code === 200) {
                        this.$message.success('新增成功')
                        this.$refs.sellerDialog.show = false
                        this.loadSellers()
                    } else {
                        this.$message.error((res && res.msg) || '新增失败')
                    }
                })
            } else {
                data.id = this.sellerForm.id
                this.api.updateInvoiceSeller(data).then(res => {
                    if (res && res.code === 200) {
                        this.$message.success('更新成功')
                        this.$refs.sellerDialog.show = false
                        this.loadSellers()
                    } else {
                        this.$message.error((res && res.msg) || '更新失败')
                    }
                })
            }
        },
        disableSeller(row) {
            this.msgConfirm('确认停用主体「' + (row.legal_name || row.id) + '」？停用后开票不会匹配到该主体。', () => {
                this.api.deleteInvoiceSeller({ id: row.id }).then(res => {
                    if (res && res.code === 200) {
                        this.$message.success('已停用')
                        this.loadSellers()
                    } else {
                        this.$message.error((res && res.msg) || '操作失败')
                    }
                })
            })
        },

        // ========== 税率 ==========
        loadTaxes() {
            this.taxLoading = true
            this.api.listInvoiceTax({}).then(res => {
                this.taxList = (res && res.code === 200 && Array.isArray(res.data)) ? res.data : []
                this.taxLoading = false
            }).catch(() => {
                this.taxList = []
                this.taxLoading = false
            })
        },
        openTaxCreate() {
            this.taxMode = 'create'
            this.taxForm = {
                id: '', country_code: '', ratePct: '',
                price_included: 1, remark: '', status: true
            }
            this.$refs.taxDialog.show = true
        },
        openTaxEdit(row) {
            this.taxMode = 'edit'
            const pct = parseFloat(row.tax_rate || '0') * 100
            this.taxForm = {
                id: row.id,
                country_code: row.country_code || '',
                ratePct: pct ? String(Math.round(pct * 100) / 100) : '',
                price_included: row.price_included === 0 ? 0 : 1,
                remark: row.remark || '',
                status: row.status === 1
            }
            this.$refs.taxDialog.show = true
        },
        submitTax() {
            if (!this.taxForm.country_code || !this.taxForm.country_code.trim()) {
                this.$message.warning('请输入国家代码')
                return
            }
            const pct = parseFloat(this.taxForm.ratePct)
            if (isNaN(pct) || pct < 0 || pct > 100) {
                this.$message.warning('请输入 0~100 之间的税率')
                return
            }
            const data = {
                country_code: this.taxForm.country_code.trim().toUpperCase(),
                tax_rate: (pct / 100).toFixed(4),
                price_included: this.taxForm.price_included,
                remark: this.taxForm.remark,
                status: this.taxForm.status ? 1 : 0
            }
            if (this.taxMode === 'edit') {
                data.id = this.taxForm.id
            }
            this.api.upsertInvoiceTax(data).then(res => {
                if (res && res.code === 200) {
                    this.$message.success('保存成功')
                    this.$refs.taxDialog.show = false
                    this.loadTaxes()
                } else {
                    this.$message.error((res && res.msg) || '保存失败')
                }
            })
        },
        disableTax(row) {
            this.msgConfirm('确认停用国家「' + row.country_code + '」的税率配置？停用后将使用内置税率。', () => {
                this.api.upsertInvoiceTax({
                    id: row.id,
                    country_code: row.country_code,
                    tax_rate: row.tax_rate,
                    price_included: row.price_included,
                    remark: row.remark,
                    status: 0
                }).then(res => {
                    if (res && res.code === 200) {
                        this.$message.success('已停用')
                        this.loadTaxes()
                    } else {
                        this.$message.error((res && res.msg) || '操作失败')
                    }
                })
            })
        }
    }
}
</script>

<style lang="less" scoped>
    .title_desc {
        margin-top: 8px;
        color: #999;
        font-size: 13px;
        line-height: 1.6;
    }
    .btn_right_box {
        text-align: right;
    }
    .form_tip {
        color: #999;
        font-size: 12px;
        margin-left: 10px;
        line-height: 1.6;
    }
    .rate_input_box {
        display: flex;
        align-items: center;
        .rate_input {
            width: 250px; // 跟随项目全局 el-input 最小宽度，避免被挤压
        }
    }
    .del_btn {
        color: #F56C6C;
    }
</style>


// WEBPACK FOOTER //
// src/view/invoice/config.vue